import type {
  HTMLAttributes,
  ReactNode,
  TdHTMLAttributes,
  ThHTMLAttributes,
} from "react";
import { cn } from "@/lib/cn";

/**
 * Dense data table. Rows are 32px, cells are 13px, numbers are tabular.
 * Wrap in <Card> for the outer border; the table itself only draws row rules.
 */
export function Table({ className, ...rest }: HTMLAttributes<HTMLTableElement>) {
  return (
    <div className="w-full overflow-x-auto">
      <table
        className={cn("w-full border-collapse text-xs", className)}
        {...rest}
      />
    </div>
  );
}

export function THead({
  className,
  ...rest
}: HTMLAttributes<HTMLTableSectionElement>) {
  return (
    <thead
      className={cn("bg-surface-sunken text-ink-muted", className)}
      {...rest}
    />
  );
}

export function TBody(props: HTMLAttributes<HTMLTableSectionElement>) {
  return <tbody {...props} />;
}

export function TR({
  selected,
  className,
  ...rest
}: HTMLAttributes<HTMLTableRowElement> & { selected?: boolean }) {
  return (
    <tr
      className={cn(
        "border-b border-border last:border-b-0",
        selected ? "bg-accent-soft" : "hover:bg-surface-sunken",
        className,
      )}
      {...rest}
    />
  );
}

export function TH({
  align = "left",
  className,
  ...rest
}: ThHTMLAttributes<HTMLTableCellElement> & { align?: "left" | "right" }) {
  return (
    <th
      scope="col"
      className={cn(
        "h-8 border-b border-border px-3 font-medium whitespace-nowrap",
        align === "right" ? "text-right" : "text-left",
        className,
      )}
      {...rest}
    />
  );
}

export function TD({
  align = "left",
  className,
  ...rest
}: TdHTMLAttributes<HTMLTableCellElement> & { align?: "left" | "right" }) {
  return (
    <td
      className={cn(
        "h-8 px-3 text-ink",
        align === "right" ? "text-right" : "text-left",
        className,
      )}
      {...rest}
    />
  );
}

/** Empty-state row — plain sentence, no illustration. */
export function TableEmpty({
  colSpan,
  children = "No records.",
}: {
  colSpan: number;
  children?: ReactNode;
}) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-3 py-6 text-center text-xs text-ink-faint">
        {children}
      </td>
    </tr>
  );
}
